import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
// import { FirebaseMessaging } from "@capacitor-firebase/messaging";
import { Geolocation } from "@capacitor/geolocation";
import { Capacitor } from "@capacitor/core";
// import { PushNotifications } from "@capacitor/push-notifications";
// import { LocalNotifications } from "@capacitor/local-notifications";
import { App as ap } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { ThemeProvider } from "./components/ui/theme-provider";
import { Toaster } from "./components/ui/toaster";
// Sonner toasts (toast.success / toast.error from "sonner"). Mounted here
// in App.js because this is the real entry rendered by the app — the
// similarly-named routes/index.tsx is unused dead code. Without this
// mount the planner's "Start the task first…" toast had nowhere to
// render and the call silently no-op'd.
import { Toaster as SonnerToaster } from "./components/ui/sonner";
import NotificationGenie from "./components/NotificationGenie";
import { AuthProvider } from "./contexts/AuthContext";
import { AccessProvider } from "./contexts/AccessContext";
import { Login } from "./pages/Login";
import { Register } from "./pages/Register";
import { Dashboard } from "./pages/Dashboard";
import { Employees } from "./pages/Employee/Employees";
import Attendance from "./pages/Attendance";
import { Leaves } from "./pages/Leaves";
import { Expenses } from "./pages/Expenses";
import Penalties from "./pages/Penalties";
import Allowances from "./pages/Allowances";
import Assets from "./pages/Assets/Assets";
// import { Tasks } from "./pages/Tasks";
import { JobPostings } from "./pages/JobPostings";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import SuperadminConsole from "./pages/Superadmin/SuperadminConsole";
import { useAuth } from "./contexts/AuthContext";
import ForgotPassword from "./pages/forgotpassword.tsx";
// import ProcessPayrollPage from "./pages/payslips";
import BulkPayrollTable from "./pages/bulk_payroll";
import AddPayroll from "./pages/AddPayroll";
import { ScreenShot } from "./pages/ScreenShot";
import { ScreenRecording } from "./pages/ScreenRecording";
import Reports from "./pages/Reports";
import Payslips from "./pages/Payslips";
import AddOffice from "./pages/AddOffice";
import AddCompany from "./pages/AddCompany";
import PayrollRecord from "./pages/PayrollRecord";
import PayrollReports from "./pages/PayrollReports";
import SalaryTaxDeclaration from "./pages/Payroll/SalaryTaxDeclaration";
import MyPayroll from "./pages/Payroll/MyPayroll";
import { ActivityList } from "./pages/ActivityList";
import ActivityDashboard from "./pages/ActivityDashboard";
// import { getFCMToken, messaging } from "./firebase";
import Documents from "./pages/Documents";
import AppDownloads from "./pages/AppDownloads";
import ActivityComparison from "./pages/ActivityComparison";
import ApprovalSettings from "./pages/ApprovalSettings";
import SettingsPage from './pages/Settings/SettingsPage';
import { CompanyPolicy } from "./pages/CompanyPolicy";
import CalendarPage from "./pages/Calendar/Calendar";
import PrivacyPolicy from "./pages/Policies/Privacy";



// Task Management Imports - Fix these paths
// import Dashboardtask from "./pages/Dashboard/Dashboard";
import TaskDashboard from './pages/TaskDashboard/TaskDashboard';
import TaskList from "./pages/Task/TaskList";
import ClientPortal from "./pages/ClientPortal/ClientPortal";
import PartnerTaskDetail from "./pages/Task/PartnerTaskDetail";
import TaskDetail from "./pages/Task/TaskDetails";
import RecentlyDeleted from "./pages/Task/RecentlyDeleted";
import TaskCreate from "./pages/Task/TaskCreate";
import TaskCreatePage from "./pages/Task/TaskCreatePage";
import ClientsPage from "./pages/Client/ClientsPage";
import VendorsPage from "./pages/Vendor/VendorsPage";
import MyTasks from "./pages/MyTasks/MyTasks";
import TeamTasks from "./pages/TeamTasks/TeamTasks";
import Notifications from "./pages/Notifications/Notifications";
import TaskReports from "./pages/TaskReports";
import TodoPlanner from "./pages/Todo/TodoPlanner";
import TodoHistory from "./pages/Todo/TodoHistory";
import ProjectsPage from "./pages/Project/ProjectsPage";
import ProjectsRecentlyDeleted from "./pages/Project/ProjectsRecentlyDeleted";
import ProjectDetails from "./pages/Project/ProjectDetails";
import TeamVisibility from "./pages/TeamVisibility/TeamVisibility";
import MeetlyWorkspace from "./pages/Meetly/MeetlyWorkspace";
import MeetlyUsageSettings from "./pages/Meetly/MeetlyUsageSettings";
import Profile from "./pages/Profile/Profile";
import EmployeeResignation from "./pages/Separation/EmployeeResignation";
// Public/marketing pages (LandingPage, About, Features, Pricing, Contact, Blog,
// Services, Privacy) now live in the separate Next.js site (web/). See
// web/deploy/teamlio.nginx.conf for how those paths are routed in production.
import LimitedWorkspaceLayout from "./limited-workspace/LimitedWorkspaceLayout";


// Separation Management Imports
import SeparationManagement from "./pages/Separation/SeparationManagement";
import ResignationList from "./components/separation/resignationlist";
import TerminationList from "./components/separation/terminationlist";
import ExitChecklist from "./components/separation/exitchecklist";


// Superadmin-only route guard. The global platform superadmin gets its own
// standalone console; anyone else is redirected to the normal dashboard, and a
// logged-out visitor to the landing page.
function SuperadminRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if ((user.role || "").toLowerCase() !== "superadmin") {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
}

function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  useLayoutEffect(() => {
    let secondFrame = 0;
    const resetScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.scrollingElement?.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      document
        .querySelectorAll("[data-route-scroll-container]")
        .forEach((element) => element.scrollTo({ top: 0, left: 0, behavior: "auto" }));
    };

    resetScroll();
    const firstFrame = window.requestAnimationFrame(() => {
      resetScroll();
      secondFrame = window.requestAnimationFrame(resetScroll);
    });
    const immediateReset = window.setTimeout(resetScroll, 0);
    const settledReset = window.setTimeout(resetScroll, 100);

    return () => {
      window.cancelAnimationFrame(firstFrame);
      if (secondFrame) window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(immediateReset);
      window.clearTimeout(settledReset);
    };
  }, [pathname, search]);

  return null;
}

function App() {
  async function askLocationPermission() {
    if (!Capacitor.isNativePlatform()) {
      console.log("🌐 Web platform → skip location permission");
      return;
    }

    const perm = await Geolocation.requestPermissions();
    console.log("LOCATION PERMISSION:", perm);
  }

  useEffect(() => {
    askLocationPermission();
    // App open pe sab notifications clear + badge reset
    // LocalNotifications.removeAllDeliveredNotifications();
    // Ya badge plugin use karo (extra install)
    // Badge.set({ badge: 0 });
  }, []);

  // useEffect(() => {
  //   const supported = FirebaseMessaging.isSupported();
  //   console.log("Supported:", supported);
  //   FirebaseMessaging.requestPermission().then((result) => {
  //     if (result.granted) {
  //       console.log("✅ FCM Permission granted");
  //       getToken(); // Token generate + save
  //     }
  //   });
  // }, []);

  // useEffect(() => {
  //   const initFCM = async () => {
  //     if (!Capacitor.isNativePlatform()) {
  //       console.log("🌐 Web detected - FCM skip");
  //       return;
  //     }

  //     try {
  //       console.log("🔍 Checking permission...");

  //       const permission = await FirebaseMessaging.requestPermission();
  //       console.log("Permission result:", permission);

  //       if (permission.granted) {
  //         const token = await FirebaseMessaging.getToken();
  //         console.log("🔥 TOKEN:", token.token);

  //         await Preferences.set({
  //           key: "fcmtoken",
  //           value: token.token,
  //         });

  //         console.log("✅ Token saved");
  //       } else {
  //         console.log("❌ Permission denied");
  //       }
  //     } catch (err) {
  //       console.log("🚫 FCM ERROR:", err);
  //     }
  //   };

  //   initFCM();
  // }, []);

  // const getToken = async () => {
  //   const token = await getFCMToken();

  //   // 👈 Global store mein save
  //   localStorage.setItem("fcmtoken", token);
  //   await Preferences.set({
  //     key: "fcmtoken1",
  //     value: token,
  //   });
  // };

  // useEffect(() => {
  //   const initPush = async () => {
  //     let permStatus = await PushNotifications.checkPermissions();
  //     console.log("Permission status:", permStatus.receive);
  //     if (permStatus.receive === "prompt") {
  //       permStatus = await PushNotifications.requestPermissions();
  //     }

  //     if (permStatus.receive === "granted") {
  //       await PushNotifications.register();
  //     }
  //   };
  //   initPush();
  // }, []);

  // useEffect(() => {
  //   initNotifications();
  //   setupAppStateListener();
  // }, []);

  // const initNotifications = async () => {
  //   // Permission request
  //   await LocalNotifications.requestPermissions();

  //   // Test notification
  //   await LocalNotifications.schedule({
  //     notifications: [
  //       {
  //         title: "HR Payroll",
  //         body: "Auto attendance active! Don't swipe from Recent apps",
  //         id: 1,
  //         badge: 0,
  //         schedule: { at: new Date(Date.now() + 5000) }, // 5 sec बाद
  //         sound: true,
  //         ongoing: true, // notification swipe न हो
  //         smallIcon: "ic_stat_notification",
  //       },
  //     ],
  //   });
  // };

  // const setupAppStateListener = () => {
  //   ap.addListener("appStateChange", ({ isActive }) => {
  //     if (!isActive) {
  //       // App background → Reminder notification
  //       LocalNotifications.schedule({
  //         notifications: [
  //           {
  //             title: "⚠️ HR Payroll Warning",
  //             body: "Clock out detected! Please open app to continue attendance.",
  //             id: 999,
  //             schedule: { at: new Date(Date.now() + 2000) },
  //             ongoing: true,
  //             sound: true,
  //           },
  //         ],
  //       });
  //     }
  //   });
  // };

  return (
    <AuthProvider>
      <AccessProvider>
      <ThemeProvider defaultTheme="light" storageKey="ui-theme">
        <Router>
          <ScrollToTop />
          <Routes>

            {/* The public landing page is now the Next.js site (web/). The SPA
                root sends visitors into the app; ProtectedRoute redirects anyone
                not signed in to /login. */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            <Route
              path="/limited-workspace"
              element={
                <ProtectedRoute>
                  <LimitedWorkspaceLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="employees" element={<Employees maxEmployees={2} limitedWorkspace />} />
              <Route path="attendance" element={<Attendance />} />
              <Route path="tasks" element={<TaskList />} />
              <Route path="planner" element={<TodoPlanner />} />
              <Route path="leave" element={<Leaves />} />
              <Route path="*" element={<Navigate to="dashboard" replace />} />
            </Route>

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            {/* Superadmin console — standalone, superadmin-only, no company Layout */}
            <Route
              path="/superadmin"
              element={
                <SuperadminRoute>
                  <SuperadminConsole />
                </SuperadminRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Dashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* Marketing routes (/about, /features, /pricing, /contact, /blog,
                /blog/:slug, /services/:slug, /privacy-policy) moved to the Next.js
                public site (web/). In production the reverse proxy sends those
                paths there — see web/deploy/teamlio.nginx.conf. */}
            {/* Public: linked from the landing page + public footer and listed in
                sitemap.xml, so it must render without a session. */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/employees"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Employees />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity-list"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ActivityList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity-reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ActivityDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/downloads"
              element={
                <ProtectedRoute>
                  <Layout>
                    <AppDownloads />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route path="/settings/:tab" element={
              <ProtectedRoute>
                <Layout>
                  <SettingsPage />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/company-policy" element={
              <ProtectedRoute>
                <Layout>
                  <CompanyPolicy />
                </Layout>
              </ProtectedRoute>
            } />

            <Route
              path="/activity-comparison"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ActivityComparison />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/leaves"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Leaves />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/penalties"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Penalties />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/allowances"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Allowances />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Expenses />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/assets"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Assets />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/company"
              element={
                //  <ProtectedRoute>
                <Layout>
                  <AddCompany />
                </Layout>
                // </ProtectedRoute>
              }
            />
            <Route
              path="/office"
              element={
                //  <ProtectedRoute>
                <Layout>
                  <AddOffice />
                </Layout>
                // </ProtectedRoute>
              }
            />
            <Route
              path="/attendance"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Attendance />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskDashboard />
                  </Layout>
                </ProtectedRoute>
              }
            />

            {/* Task Management - New Routes */}
            <Route
              path="/tasks/list"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/client-portal"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ClientPortal />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/partner-task/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PartnerTaskDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/my-tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/team-tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskList />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/employee-tasks"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TeamVisibility />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/tasks/reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskReports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/create"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskCreatePage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/recently-deleted"
              element={
                <ProtectedRoute>
                  <Layout>
                    <RecentlyDeleted />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/meetly"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MeetlyWorkspace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            {/* History is now the sidebar's meeting list, so this old route
                just folds back into the workspace. */}
            <Route path="/meetly/history" element={<Navigate to="/meetly" replace />} />
            <Route
              path="/meetly/usage"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MeetlyUsageSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/meetly/meetings/:meetingId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MeetlyWorkspace />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/:taskId"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TaskDetail />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ClientsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <Layout>
                    <VendorsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectsPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/recently-deleted"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectsRecentlyDeleted />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ProjectDetails />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/tasks/notifications"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Notifications />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/todo"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TodoPlanner />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/todo/history"
              element={
                <ProtectedRoute>
                  <Layout>
                    <TodoHistory />
                  </Layout>
                </ProtectedRoute>
              }
            />


            {/* Separation Management Routes */}
            <Route
              path="/separation/dashboard"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SeparationManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/separation/resignations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SeparationManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/separation/terminations"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SeparationManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/separation/checklists"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SeparationManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/separation/settlements"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SeparationManagement />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/my-resignation"
              element={
                <ProtectedRoute>
                  <Layout>
                    <EmployeeResignation />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/documents"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Documents />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/jobs"
              element={
                <ProtectedRoute>
                  <Layout>
                    <JobPostings />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/Payslips"
              element={
                <Layout>
                  <Payslips />
                </Layout>
              }
            />
            <Route
              path="/payroll-record"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollRecord />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/payroll/reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <PayrollReports />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-payroll"
              element={
                <ProtectedRoute>
                  <Layout>
                    <MyPayroll />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/salary-tax-declaration"
              element={
                <ProtectedRoute>
                  <Layout>
                    <SalaryTaxDeclaration />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/approval-settings"
              element={
                <ProtectedRoute>
                  <Layout>
                    <ApprovalSettings />
                  </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/screen-shot"
              element={
                 <ProtectedRoute>
                <Layout>
                  <ScreenShot />
                </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/screen-recording"
              element={
                 <ProtectedRoute>
                <Layout>
                  <ScreenRecording />
                </Layout>
                </ProtectedRoute>
              }
            />
            <Route
              path="/AddPayroll"
              element={
                <Layout>
                  <AddPayroll />
                </Layout>
              }
            />

            <Route path="/profile" element={
              <ProtectedRoute>
                <Layout>
                  <Profile />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* // <Route path="/payroll/records" element={<PayrollRecords />} />
            // <Route path="/payroll/payslips" element={<Payslips />} />
            // <Route path="/payroll/tds" element={<TDS />} />
            // <Route path="/payroll/reports" element={<Reports />} />  */}

            <Route
              path="/reports"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Reports />
                  </Layout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Layout>
                    <CalendarPage />
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
          {/* Toaster lives INSIDE <Router> so its DismissOnNavigate child
              can subscribe to location changes and auto-close popups when
              the user leaves the current page. */}
          <Toaster />
          {/* Sonner toasts — used by TodoPlanner, Checklist, RecentlyDeleted,
              QuickAdd, useReminderStream via `import { toast } from "sonner"`.
              Without this mount every `toast.success / toast.error` call from
              those modules is silently dropped (Sonner needs its own
              Toaster). top-right + richColors so errors show with a red
              background and successes with a green one. */}
          <SonnerToaster position="top-right" richColors closeButton />
          {/* Genie dismissal: sucks any dismissing top-right toast into the
              notification bell (transform/opacity only, reduced-motion aware). */}
          <NotificationGenie />
        </Router>
      </ThemeProvider>
      </AccessProvider>
    </AuthProvider>
  );
}

export default App;
